import { Box, Typography, useTheme } from "@mui/material";

/**
 * Renders a compact legend for row status shading (e.g. Tagged, Kit).
 * Touch-friendly and readable at 100% zoom / tablet.
 */
export default function StatusLegend({ items = [] }) {
  const theme = useTheme();
  const { text = {}, divider, mode } = theme.palette || {};
  const isDark = mode === "dark";
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const labelColor = isDark ? textSecondary : "#475569";
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0");
  const pillBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.8)";
  const pillBorder = isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0,0,0,0.06)";
  const pillShadow = isDark ? "0 1px 2px rgba(0,0,0,0.2)" : "0 1px 2px rgba(0,0,0,0.04)";

  if (!items || items.length === 0) return null;

  return (
    <Box
      component="section"
      aria-label="Row status legend"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: "wrap",
        py: 0.375,
        px: 0,
      }}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{
          color: labelColor,
          fontWeight: 600,
          letterSpacing: "0.04em",
          fontSize: "0.75rem",
          textTransform: "uppercase",
          opacity: isDark ? 0.9 : 1,
        }}
      >
        Row status
      </Typography>
      {items.map(({ label, color }) => (
        <Box
          key={label}
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            padding: "4px 8px",
            borderRadius: "6px",
            backgroundColor: pillBg,
            border: `1px solid ${pillBorder}`,
            boxShadow: pillShadow,
            transition: "box-shadow 0.2s ease, border-color 0.2s ease",
            "&:hover": {
              boxShadow: isDark ? "0 2px 4px rgba(0,0,0,0.25)" : "0 2px 4px rgba(0,0,0,0.06)",
              borderColor: isDark ? "rgba(148, 163, 184, 0.25)" : "rgba(0,0,0,0.08)",
            },
          }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 7,
              height: 7,
              borderRadius: "2px",
              backgroundColor: color,
              boxShadow: `0 0 0 1px rgba(0,0,0,0.12)`,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: textSecondary, fontSize: "0.75rem", fontWeight: 500 }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
